// Cookie Consent Banner Component
// Displays privacy-compliant consent banner for Google Analytics

import React, { useState, useEffect } from 'react';
import { Button } from '../elements/Button';
import { 
  AnalyticsConsent, 
  getAnalyticsConsent, 
  setAnalyticsConsent, 
  hasOptedOut 
} from '../../../utils/analytics';
import './CookieConsent.css';

interface CookieConsentProps {
  onConsentChange?: (consent: AnalyticsConsent) => void;
}

const CookieConsent: React.FC<CookieConsentProps> = ({ onConsentChange }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Show banner if consent is pending and user hasn't opted out
    const consent = getAnalyticsConsent();
    const shouldShow = consent === AnalyticsConsent.PENDING && !hasOptedOut();
    setShowBanner(shouldShow);
  }, []);

  const handleAccept = () => {
    setAnalyticsConsent(AnalyticsConsent.GRANTED);
    setShowBanner(false);
    onConsentChange?.(AnalyticsConsent.GRANTED);
  };

  const handleDecline = () => {
    setAnalyticsConsent(AnalyticsConsent.DENIED);
    setShowBanner(false);
    onConsentChange?.(AnalyticsConsent.DENIED);
  };

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  if (!showBanner) return null;

  return (
    <div className="cookie-consent-banner">
      <div className="cookie-consent-content">
        <div className="cookie-consent-text">
          <h4>We value your privacy</h4>
          <p>
            We use cookies and analytics to improve your experience on our website. 
            This helps us understand how you interact with our site and improve our services.
          </p>
          
          {showDetails && (
            <div className="cookie-consent-details">
              <h5>What we collect:</h5>
              <ul>
                <li>Page views and navigation patterns (anonymized)</li>
                <li>General location (country/region level only)</li>
                <li>Device and browser information</li>
                <li>How you interact with our content</li>
              </ul>
              
              <h5>What we don't collect:</h5>
              <ul>
                <li>Personal information without your consent</li>
                <li>Exact location or IP addresses</li>
                <li>Information for advertising purposes</li>
              </ul>
              
              <p>
                You can change your preferences at any time in our privacy settings. 
                For more information, see our <a href="/privacy-policy">Privacy Policy</a>.
              </p>
            </div>
          )}
        </div>
        
        <div className="cookie-consent-actions">
          <div className="cookie-consent-buttons">
            <Button
              props={{
                text: "Accept Analytics",
                onClick: handleAccept,
                classes: "cookie-accept"
              }}
            />
            <Button
              props={{
                text: "Decline",
                onClick: handleDecline,
                classes: "cookie-decline"
              }}
            />
          </div>
          
          <button 
            className="cookie-details-toggle"
            onClick={toggleDetails}
            type="button"
          >
            {showDetails ? 'Hide Details' : 'Learn More'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;